import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { isOverviewAdjuster, isOverviewPrivileged } from '../lib/portalRoles';
import { fetchOverviewUsers, fetchReferralOverview, } from '../services/referrals';
import { ensureSignInUserInfo } from '../services/signinUserInfo';
const STORAGE_KEY = 'wc_portal_dashboard_overview_open';
const SCOPE_STORAGE_PREFIX = 'wc_portal_dashboard_overview_scope:';
const RANGE_STORAGE_PREFIX = 'wc_portal_dashboard_overview_range:';
const RANGE_OPTIONS = [
    { value: '6m', label: '6 months' },
    { value: '1m', label: '1 month' },
    { value: '1w', label: '1 week' },
];
function rangeLabel(range) {
    return RANGE_OPTIONS.find((option) => option.value === range)?.label ?? '6 months';
}
function parseStoredRange(value) {
    return value === '6m' || value === '1m' || value === '1w' ? value : null;
}
function readStoredOpen() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw === '1' || raw === 'true')
            return true;
        if (raw === '0' || raw === 'false')
            return false;
    }
    catch {
        // localStorage may be unavailable
    }
    return false;
}
function writeStoredOpen(open) {
    try {
        window.localStorage.setItem(STORAGE_KEY, open ? '1' : '0');
    }
    catch {
        // ignore quota / private mode
    }
}
function scopeStorageKey(userId, roleClass) {
    return `${SCOPE_STORAGE_PREFIX}${userId}:${roleClass}`;
}
function readStoredScope(userId, roleClass) {
    try {
        const raw = window.localStorage.getItem(scopeStorageKey(userId, roleClass));
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (parsed.mode !== 'org' && parsed.mode !== 'mine' && parsed.mode !== 'user') {
            return null;
        }
        return {
            mode: parsed.mode,
            createdBy: typeof parsed.createdBy === 'number' && Number.isFinite(parsed.createdBy)
                ? parsed.createdBy
                : null,
        };
    }
    catch {
        return null;
    }
}
function writeStoredScope(userId, roleClass, scope) {
    try {
        window.localStorage.setItem(scopeStorageKey(userId, roleClass), JSON.stringify(scope));
    }
    catch {
        // ignore
    }
}
function readStoredRange(userId) {
    try {
        const raw = window.localStorage.getItem(`${RANGE_STORAGE_PREFIX}${userId}`);
        return parseStoredRange(raw) ?? '6m';
    }
    catch {
        return '6m';
    }
}
function writeStoredRange(userId, range) {
    try {
        window.localStorage.setItem(`${RANGE_STORAGE_PREFIX}${userId}`, range);
    }
    catch {
        // ignore
    }
}
function formatMetric(value, suffix = '') {
    if (value == null || !Number.isFinite(value))
        return '—';
    return `${value}${suffix}`;
}
function userDisplayName(user) {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    if (name)
        return name;
    if (user.email)
        return user.email;
    return `User ${user.id}`;
}
function sortUsersByDisplayName(users) {
    return [...users].sort((a, b) => {
        const cmp = userDisplayName(a).localeCompare(userDisplayName(b), undefined, {
            sensitivity: 'base',
        });
        if (cmp !== 0)
            return cmp;
        return a.id - b.id;
    });
}
function MetricCard({ label, value, hint, }) {
    return (_jsxs("div", { className: "overview-metric", children: [_jsx("span", { className: "overview-metric-label", children: label }), _jsx("span", { className: "overview-metric-value mono", children: value }), hint ? _jsx("span", { className: "overview-metric-hint", children: hint }) : null] }));
}
export function DashboardOverview() {
    const panelId = useId();
    const [open, setOpen] = useState(readStoredOpen);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [stats, setStats] = useState(null);
    const [loadedOnce, setLoadedOnce] = useState(false);
    const [viewerId, setViewerId] = useState(null);
    const [viewerRoleName, setViewerRoleName] = useState('');
    const [roleReady, setRoleReady] = useState(false);
    const privileged = isOverviewPrivileged(viewerRoleName);
    const adjuster = isOverviewAdjuster(viewerRoleName);
    const [scopeMode, setScopeMode] = useState('org');
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [range, setRange] = useState('6m');
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersError, setUsersError] = useState('');
    const [usersLoaded, setUsersLoaded] = useState(false);
    useEffect(() => {
        let cancelled = false;
        ensureSignInUserInfo()
            .then((info) => {
            if (cancelled)
                return;
            const detail = info.detail;
            const id = typeof detail?.id === 'number' ? detail.id : null;
            const roleName = detail?.roleName?.trim() ||
                (typeof detail?.role?.name === 'string' ? detail.role.name.trim() : '') ||
                '';
            setViewerId(id);
            setViewerRoleName(roleName);
            setRoleReady(true);
            if (id == null)
                return;
            setRange(readStoredRange(id));
            if (isOverviewPrivileged(roleName)) {
                const stored = readStoredScope(id, 'privileged');
                if (stored) {
                    setScopeMode(stored.mode === 'mine' ? 'org' : stored.mode);
                    setSelectedUserId(stored.mode === 'user' ? stored.createdBy : null);
                }
                else {
                    setScopeMode('org');
                    setSelectedUserId(null);
                }
            }
            else if (isOverviewAdjuster(roleName)) {
                const stored = readStoredScope(id, 'adjuster');
                if (stored?.mode === 'mine' || stored?.mode === 'org') {
                    setScopeMode(stored.mode);
                    setSelectedUserId(stored.mode === 'mine' ? id : null);
                }
                else {
                    setScopeMode('mine');
                    setSelectedUserId(id);
                }
            }
        })
            .catch((err) => {
            console.error('[dashboard-overview] signin user failed', err);
            if (!cancelled)
                setRoleReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    const sortedUsers = useMemo(() => sortUsersByDisplayName(users), [users]);
    const createdByForRequest = useMemo(() => {
        if (privileged) {
            return scopeMode === 'user' && selectedUserId != null ? selectedUserId : null;
        }
        if (adjuster) {
            return scopeMode === 'mine' && viewerId != null ? viewerId : null;
        }
        return null;
    }, [privileged, adjuster, scopeMode, selectedUserId, viewerId]);
    const persistScope = useCallback((next) => {
        if (viewerId == null)
            return;
        if (privileged)
            writeStoredScope(viewerId, 'privileged', next);
        else if (adjuster)
            writeStoredScope(viewerId, 'adjuster', next);
    }, [viewerId, privileged, adjuster]);
    const loadOverview = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const overview = await fetchReferralOverview({
                createdBy: createdByForRequest,
                range,
            });
            setStats(overview);
            setLoadedOnce(true);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load overview.');
        }
        finally {
            setLoading(false);
        }
    }, [createdByForRequest, range]);
    const loadUsers = useCallback(async () => {
        setUsersLoading(true);
        setUsersError('');
        try {
            const list = await fetchOverviewUsers();
            setUsers(list);
            setUsersLoaded(true);
        }
        catch (err) {
            setUsersError(err instanceof Error ? err.message : 'Failed to load users.');
            setUsersLoaded(true);
        }
        finally {
            setUsersLoading(false);
        }
    }, []);
    useEffect(() => {
        if (!open || !roleReady)
            return;
        void loadOverview();
    }, [open, roleReady, loadOverview]);
    useEffect(() => {
        if (privileged && !usersLoaded && !usersLoading) {
            void loadUsers();
        }
    }, [privileged, usersLoaded, usersLoading, loadUsers]);
    const toggle = () => {
        setOpen((prev) => {
            const next = !prev;
            writeStoredOpen(next);
            return next;
        });
    };
    const onPrivilegedUserChange = (value) => {
        if (value === '' || value === 'org') {
            setScopeMode('org');
            setSelectedUserId(null);
            persistScope({ mode: 'org', createdBy: null });
            return;
        }
        const id = Number(value);
        if (!Number.isFinite(id) || id <= 0)
            return;
        setScopeMode('user');
        setSelectedUserId(id);
        persistScope({ mode: 'user', createdBy: id });
    };
    const onAdjusterScopeChange = (value) => {
        if (value === 'mine' && viewerId != null) {
            setScopeMode('mine');
            setSelectedUserId(viewerId);
            persistScope({ mode: 'mine', createdBy: viewerId });
            return;
        }
        setScopeMode('org');
        setSelectedUserId(null);
        persistScope({ mode: 'org', createdBy: null });
    };
    const onRangeChange = (value) => {
        const next = parseStoredRange(value);
        if (!next)
            return;
        setRange(next);
        if (viewerId != null)
            writeStoredRange(viewerId, next);
    };
    const stopHeaderControlToggle = (event) => {
        event.stopPropagation();
    };
    const windowCountLabel = `Last ${rangeLabel(range)}`;
    const subtitleParts = [
        stats ? `${stats.counts.last6Months} referrals` : null,
    ].filter(Boolean);
    const scopeControls = (_jsxs(_Fragment, { children: [_jsxs("label", { className: "overview-scope-field overview-scope-field-compact", children: [_jsx("span", { className: "overview-scope-label", children: "Period" }), _jsx("select", { className: "overview-scope-select", value: range, onChange: (e) => onRangeChange(e.target.value), children: RANGE_OPTIONS.map((option) => (_jsx("option", { value: option.value, children: option.label }, option.value))) })] }), privileged ? (_jsxs("label", { className: "overview-scope-field overview-scope-field-compact", children: [_jsx("span", { className: "overview-scope-label", children: "Show stats for" }), _jsxs("select", { className: "overview-scope-select", value: scopeMode === 'user' && selectedUserId != null ? String(selectedUserId) : 'org', onChange: (e) => onPrivilegedUserChange(e.target.value), disabled: usersLoading, children: [_jsx("option", { value: "org", children: "Entire organization" }), sortedUsers.map((user) => (_jsxs("option", { value: user.id, children: [userDisplayName(user), user.roleName ? ` (${user.roleName})` : ''] }, user.id)))] })] })) : null, adjuster ? (_jsxs("label", { className: "overview-scope-field overview-scope-field-compact", children: [_jsx("span", { className: "overview-scope-label", children: "Show stats for" }), _jsxs("select", { className: "overview-scope-select", value: scopeMode === 'mine' ? 'mine' : 'org', onChange: (e) => onAdjusterScopeChange(e.target.value), children: [_jsx("option", { value: "mine", children: "My referrals" }), _jsx("option", { value: "org", children: "Entire organization" })] })] })) : null] }));
    return (_jsxs("section", { className: `dashboard-overview${open ? ' is-open' : ''}`, children: [_jsxs("div", { className: "dashboard-overview-header", children: [_jsxs("button", { type: "button", className: "dashboard-overview-toggle", onClick: toggle, "aria-expanded": open, "aria-controls": panelId, children: [_jsx("span", { className: "dashboard-overview-title", children: "Overview" }), _jsx("span", { className: "dashboard-overview-subtitle", children: subtitleParts.join(' · ') }), _jsx("span", { className: "dashboard-overview-chevron", "aria-hidden": "true", children: open ? '▾' : '▸' })] }), _jsx("div", { className: "dashboard-overview-controls", onClick: stopHeaderControlToggle, onMouseDown: stopHeaderControlToggle, onPointerDown: stopHeaderControlToggle, children: scopeControls })] }), open && (_jsxs("div", { id: panelId, className: "dashboard-overview-body", children: [privileged && (usersLoading || usersError || (usersLoaded && users.length === 0)) ? (_jsxs("div", { className: "overview-scope-row", children: [usersLoading ? (_jsx("span", { className: "overview-scope-hint", children: "Loading users\u2026" })) : null, !usersLoading && usersLoaded && users.length === 0 && !usersError ? (_jsxs("span", { className: "overview-scope-hint", children: ["No users returned from directory.", ' ', _jsx("button", { type: "button", className: "btn btn-ghost", onClick: () => void loadUsers(), children: "Retry" })] })) : null, usersError ? (_jsxs("span", { className: "overview-scope-hint overview-scope-hint-error", children: [usersError, ' ', _jsx("button", { type: "button", className: "btn btn-ghost", onClick: () => void loadUsers(), children: "Retry" })] })) : null] })) : null, loading && !stats ? (_jsx("p", { className: "overview-status", children: "Loading overview\u2026" })) : error && !stats ? (_jsxs("div", { className: "overview-status overview-status-error", children: [_jsx("p", { children: error }), _jsx("button", { type: "button", className: "btn btn-ghost", onClick: () => void loadOverview(), children: "Retry" })] })) : stats ? (_jsxs(_Fragment, { children: [error ? _jsx("p", { className: "overview-status overview-status-error", children: error }) : null, _jsxs("div", { className: "overview-metric-grid", children: [range === '6m' || range === '1m' ? (_jsx(MetricCard, { label: "Last 7 days", value: formatMetric(stats.counts.last7Days) })) : null, range === '6m' ? (_jsx(MetricCard, { label: "Last 30 days", value: formatMetric(stats.counts.last30Days) })) : null, _jsx(MetricCard, { label: windowCountLabel, value: formatMetric(stats.counts.last6Months), hint: stats.totalInWindow > stats.sampleSize
                                            ? `Showing ${stats.sampleSize} of ${stats.totalInWindow}`
                                            : undefined }), _jsx(MetricCard, { label: "Avg resolution", value: stats.avgResolutionDays == null
                                            ? '—'
                                            : `${stats.avgResolutionDays} day${stats.avgResolutionDays === 1 ? '' : 's'}`, hint: "Closed / rejected" }), stats.statusCounts.map((entry) => (_jsx(MetricCard, { label: entry.name, value: formatMetric(entry.count) }, entry.value))), _jsx(MetricCard, { label: "Needs approval", value: formatMetric(stats.counts.needsApproval) }), _jsx(MetricCard, { label: "Avg attachments", value: formatMetric(stats.avgAttachments) })] }), loadedOnce && (_jsx("div", { className: "overview-footer", children: _jsx("button", { type: "button", className: "btn btn-ghost", disabled: loading, onClick: () => void loadOverview(), children: loading ? 'Refreshing…' : 'Refresh' }) }))] })) : (_jsx("p", { className: "overview-status", children: "No overview data yet." }))] }))] }));
}
